import { t } from '@avalara/bat-apps-shared/i18n';
import { SBreadcrumbs, SCardAi, SCol, SContainer, SRow } from '@avalara/skylab-sdk/react';
import { Link, useHistory, useLocation } from 'react-router-dom';

import '../product-catalog.css';
import { trackCtaClick, usePricingCtaClick } from './use-pricing-cta';

import type { TranslationKey } from '@avalara/bat-apps-shared/i18n';
import conf from '../../../conf';
import { appendQueryParamToUrl } from '../../helpers/redirect';

import type { ProductCatalogItem } from '../../../../../types/product-catalog';
import type { ProductAiInsight, ProductHeroPricing } from '../../../../../types/product-detail';

type Props = {
  item: ProductCatalogItem;
  breadcrumbLabel?: string;
  heroTitle?: string;
  heroImageUrl?: string;
  aiInsight?: ProductAiInsight;
  heroPricing?: ProductHeroPricing;
  hasPricing: boolean;
  onContactClick: () => void;
};

const ProductDetailHero = ({
  item,
  breadcrumbLabel,
  heroTitle,
  heroImageUrl,
  aiInsight,
  heroPricing,
  hasPricing,
  onContactClick,
}: Props) => {
  const history = useHistory();
  const location = useLocation();
  const handleCtaClick = usePricingCtaClick(onContactClick);
  const primaryCta = heroPricing?.primaryCta;
  const secondaryCta = heroPricing?.secondaryCta;

  const handleBreadcrumbNav = (_event: Event, targetHref: string) => history.push(targetHref);

  return (
    <div className='product-detail-hero-banner pad-start-xl pad-end-xl'>
      <div className='product-detail-hero-bg-layer' aria-hidden='true' />
      <SContainer data-ref='buy.product-detail.hero' class='pad-all-none'>
        <SRow>
          <SCol className='pad-all-none' span={heroImageUrl ? '7' : '12'}>
            {/* The catalog url is an absolute path including the /buy basename on purpose:
                SBreadcrumbs hands navHandler the raw href and does NOT preventDefault when a
                handler is present, so a real browser navigation to this href happens too.
                Forwards this page's own query params (e.g. ?shouldShowProductCatalog=true)
                instead of hardcoding one — only carries over what's actually present. */}
            <SBreadcrumbs
              navItems={[
                {
                  label: t('client.product-detail.breadcrumb.catalog'),
                  url: appendQueryParamToUrl('/buy/product-catalog', location.search.slice(1)),
                  emitNavEvent: true,
                },
                { label: breadcrumbLabel ? t(breadcrumbLabel as TranslationKey) : item.title },
              ]}
              navHandler={handleBreadcrumbNav}
              data-ref='buy.product-detail.hero.breadcrumb'
            />
            <h1 className='pad-top-sm pad-bottom-md' data-ref='buy.product-detail.hero.title'>
              {heroTitle ? t(heroTitle as TranslationKey) : item.title}
            </h1>
            <p data-ref='buy.product-detail.hero.description' className='pad-bottom-sm'>
              {t(item.description as TranslationKey)}
            </p>
            {hasPricing && (
              <a
                className='product-detail-hero-pricing-link'
                href='#buy-product-detail-pricing'
                data-ref='buy.product-detail.hero.see-pricing-link'
              >
                {t('client.product-detail.see-pricing-below')}
              </a>
            )}
            {heroPricing && (
              <div className='product-detail-hero-pricing' data-ref='buy.product-detail.hero.pricing'>
                {heroPricing.price && (
                  <p className='font-bold' data-ref='buy.product-detail.hero.pricing.price'>
                    <span className='text-md'>{heroPricing.price}</span>
                    {heroPricing.priceSubtext && <span className='text-md'> {heroPricing.priceSubtext}</span>}
                  </p>
                )}
                {heroPricing.bodyText && (
                  <p className='pad-top-sm pad-bottom-sm' data-ref='buy.product-detail.hero.pricing.body-text'>
                    {heroPricing.bodyText}
                  </p>
                )}
                <div className='flex' data-ref='buy.product-detail.hero.pricing.ctas'>
                  {primaryCta &&
                    (primaryCta.ctaAction === 'buy' && primaryCta.offerFlow ? (
                      // A real link (not a client-side-only button) since this genuinely navigates
                      // to another route — matches skylab's own a.button.primary styling contract.
                      <Link
                        to={`/${primaryCta.offerFlow}`}
                        className='button primary'
                        data-ref='buy.product-detail.hero.pricing.primary-cta'
                        onClick={() => trackCtaClick('hero-primary')}
                      >
                        {primaryCta.ctaText}
                      </Link>
                    ) : (
                      <button
                        type='button'
                        className='primary'
                        data-ref='buy.product-detail.hero.pricing.primary-cta'
                        onClick={() => handleCtaClick('hero-primary', primaryCta)}
                      >
                        {primaryCta.ctaText}
                      </button>
                    ))}
                  {secondaryCta && (
                    <button
                      type='button'
                      className='secondary'
                      data-ref='buy.product-detail.hero.pricing.secondary-cta'
                      onClick={() => handleCtaClick('hero-secondary', secondaryCta)}
                    >
                      {secondaryCta.ctaText}
                    </button>
                  )}
                </div>
              </div>
            )}
            {aiInsight && (
              <SCardAi className='product-detail-hero-insight' data-ref='buy.product-detail.hero.ai-insight'>
                <span slot='header-title' data-ref='buy.product-detail.hero.ai-insight.title'>
                  {t(aiInsight.headerTitle as TranslationKey)}
                </span>
                <span slot='header-badge' data-ref='buy.product-detail.hero.ai-insight.badge'>
                  {t(aiInsight.badge as TranslationKey)}
                </span>
                <p
                  slot='content'
                  data-ref='buy.product-detail.hero.ai-insight.body'
                  className='pad-top-sm pad-bottom-sm'
                >
                  {t(aiInsight.body as TranslationKey)}
                </p>
              </SCardAi>
            )}
          </SCol>
          {heroImageUrl && (
            <SCol span='5' className='flex align-items-center justify-content-center'>
              <img
                className='product-detail-hero-image'
                src={`${conf.ASSET_PATH}/assets/images/product-detail/${heroImageUrl}`}
                alt=''
                data-ref='buy.product-detail.hero.image'
              />
            </SCol>
          )}
        </SRow>
      </SContainer>
    </div>
  );
};

export default ProductDetailHero;
